/**
 *
 * App
 *
 * This component is the skeleton around the actual pages, and should only
 * contain code that should be seen on all pages. (e.g. navigation bar)
 */

import * as React from "react";
import { Helmet } from "react-helmet-async";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import { stripePublicKey } from "utils/api/endpoints";
import { createAbsoluteUrl } from "utils/constants";
import { useGlobalSlice } from "./slice";
import { ThemeProvider } from "./components/ui/theme-provider";
import { Toaster } from "./components/ui/toaster";
import { TooltipProvider } from "./components/ui/tooltip";

import { AdminLayout } from "./components/AdminLayout";
import { Login } from "./pages/Customer/Login/Loadable";
import { ForgotPassword } from "./pages/Customer/ForgotPassword/Loadable";
import { Home } from "./pages/Customer/Home/Loadable";
import { PlatformDrivers } from "./pages/Customer/PlatformDrivers/Loadable";
import { PlansAndPricing } from "./pages/Customer/PlansAndPricing/Loadable";
import { HowToSwitch } from "./pages/Customer/HowToSwitch/Loadable";
import { AboutVLEMobile } from "./pages/Customer/AboutVLEMobile/Loadable";
import { ScheduleCall } from "./pages/Customer/ScheduleCall/Loadable";
import { NetworkCoverage } from "./pages/Customer/NetworkCoverage/Loadable";
import { InternationalCalls } from "./pages/Customer/InternationalCalls/Loadable";
import { SupportCenter } from "./pages/Customer/SupportCenter/Loadable";
import { IMEICheck } from "./pages/Customer/IMEICheck/Loadable";
import { AbouteSIM } from "./pages/Customer/AbouteSIM/Loadable";
import { HowVLEMobileWorks } from "./pages/Customer/HowVLEMobileWorks/Loadable";
import { FAQ } from "./pages/Customer/FAQ/Loadable";
import { ResourceCenter } from "./pages/Customer/ResourceCenter/Loadable";
import { Blog } from "./pages/Customer/Blog/Loadable";
import { BlogDetail } from "./pages/Customer/BlogDetail/Loadable";
import { WhyChooseVLEMobile } from "./pages/Customer/WhyChooseVLEMobile/Loadable";
import { HomePhone } from "./pages/Customer/HomePhone/Loadable";
import { ListPage } from "./pages/Customer/HomePhone/components/ListPage/Loadable";
import { ConsumeCellular } from "./pages/Customer/HomePhone/components/ConsumeCellular/Loadable";
import { HomePhoneCheckout } from "./pages/Customer/HomePhone/components/Checkout/Loadable";
import { DataTopup } from "./pages/Customer/DataTopup/Loadable";
import { FamilyPlans } from "./pages/Customer/FamilyPlans/Loadable";
import { BroadbandFacts } from "./pages/Customer/BroadbandFacts/Loadable";
import { DigitalSecurity } from "./pages/Customer/DigitalSecurity/Loadable";
import { DigitalSecuritySelection } from "./pages/Customer/DigitalSecurity/DigitalSecuritySelection/Loadable";
import { SIMSwap } from "./pages/Customer/SIMSwap/Loadable";
import { NetworkPlan } from "./pages/Customer/NetworkPlan/Loadable";
import { Activation } from "./pages/Customer/Activation/Loadable";
import { QuickRecharge } from "./pages/Customer/QuickRecharge/Loadable";
import { Support } from "./pages/Customer/Support/Loadable";
import { SubmitTicket } from "./pages/Customer/SubmitTicket/Loadable";
import { ManageTicket } from "./pages/Customer/ManageTicket/Loadable";
import { Disclosure } from "./pages/Customer/Disclosure/Loadable";
import { NetworkPolicy } from "./pages/Customer/NetworkPolicy/Loadable";
import { Accessibility } from "./pages/Customer/Accessibility/Loadable";
import { ReturnPolicy } from "./pages/Customer/ReturnPolicy/Loadable";
import { TermsAndConditions } from "./pages/Customer/TermsAndConditions/Loadable";
import { PrivacyPolicy } from "./pages/Customer/PrivacyPolicy/Loadable";
import { DeletionPolicy } from "./pages/Customer/DeletionPolicy/Loadable";
import { DoNotSellMyData } from "./pages/Customer/DoNotSellMyData/Loadable";
import { SingleNetworkSelection } from "./pages/Customer/SingleNetworkSelection/Loadable";
import { MakeASelection } from "./pages/Customer/SingleNetworkSelection/components/make-a-selection";
import { DeviceCompatibilty } from "./pages/Customer/SingleNetworkSelection/components/device-compatibility";
import { TypeOfNumber } from "./pages/Customer/SingleNetworkSelection/components/type-of-number";
import { MultiNetworkSelection } from "./pages/Customer/MultiNetworkSelection/Loadable";
import { SelectionAndCoverage } from "./pages/Customer/MultiNetworkSelection/components/selection-and-coverage";
import { PhoneType } from "./pages/Customer/MultiNetworkSelection/components/phone-type";
import { NumberType } from "./pages/Customer/MultiNetworkSelection/components/number-type";
import { NumberInformation } from "./pages/Customer/MultiNetworkSelection/components/number-information";
import { Checkout } from "./pages/Customer/Checkout/Loadable";
import { MyAccountLayout } from "./components/AdminLayout/MyAccountLayout";
import { MobileLineDetailsProvider } from "./components/AdminLayout/MyAccountLayout/mobile-line-details-context";
import { Dashboard } from "./pages/Customer/Dashboard/Loadable";

// External redirect component
const ExternalRedirect = () => {
	React.useEffect(() => {
		window.location.href = createAbsoluteUrl();
	}, []);
	
	return (
		<></>
	);
};

import { PaymentHistory } from "./pages/Customer/PaymentHistory/Loadable";
import { ManageProfile } from "./pages/Customer/ManageProfile/Loadable";
import { ChangePassword } from "./pages/Customer/ChangePassword/Loadable";
import { SimService } from "./pages/Customer/SimService/Loadable";
import { Login as AgentLogin } from "./pages/Agent/Login/Loadable";
import { CustomerDashboard } from "./pages/Agent/CustomerDashboard/Loadable";
import { TwoFactorAuthentication } from "./pages/Agent/TwoFactorAuthentication/Loadable";
import { SimUnlockRequest } from "./pages/Agent/SimUnlockRequest/Loadable";
import { SwapData } from "./pages/Agent/SwapData/Loadable";
import { SwapVoice } from "./pages/Agent/SwapVoice/Loadable";
import { NumberChange } from "./pages/Agent/NumberChange/Loadable";
import { UpdateProfile } from "./pages/Agent/UpdateProfile/Loadable";
import { UpdatePaymentMethod } from "./pages/Agent/UpdatePaymentMethod/Loadable";
import { UpgradePlan } from "./pages/Agent/UpgradePlan/Loadable";
import { AddLine } from "./pages/Agent/AddLine/Loadable";
import { CreateSupportTicket } from "./pages/Agent/CreateSupportTicket/Loadable";
import { NotFoundPage } from "./pages/NotFoundPage/Loadable";

export function App() {
	const stripePromise = loadStripe(stripePublicKey);
	const { i18n } = useTranslation();
	useGlobalSlice();
	
	return (
		<BrowserRouter>
			<ThemeProvider defaultTheme="light" storageKey="vle-theme">
				<TooltipProvider>
					<Helmet
						titleTemplate="%s - VLE Mobile"
						defaultTitle="VLE Mobile"
						htmlAttributes={{ lang: i18n.language }}
					>
						<meta name="description" content="VLE Mobile" />
					</Helmet>
					<Elements stripe={stripePromise}>
						<Routes>
							<Route element={<AdminLayout />}>
								{/* <Route path="/" element={<Home />} /> */}
								<Route path="/" element={<ExternalRedirect />} />
								<Route path="/platform-drivers" element={<PlatformDrivers />} />
								<Route path="/plans-and-pricing" element={<PlansAndPricing />} />
								<Route path="/how-to-switch" element={<HowToSwitch />} />
								<Route path="/about-us" element={<AboutVLEMobile />} />
								<Route path="/how-vle-mobile-works" element={<HowVLEMobileWorks />} />
								{/* <Route path="/faq" element={<FAQ />} />
								<Route path="/resource-center" element={<ResourceCenter />} />
								<Route path="/blog" element={<Blog />} />
								<Route path="/blog-detail" element={<BlogDetail />} />
								<Route path="/support-center" element={<SupportCenter />} />
								<Route path="/support-tickets" element={<Support />} /> */}
								<Route path="/support-tickets/submit-ticket" element={<SubmitTicket />} />
								{/* <Route path="/support-tickets/manage-ticket" element={<ManageTicket />} /> */}
								<Route path="/why-choose-vle-mobile" element={<WhyChooseVLEMobile />} />
								<Route path="/home-phone" element={<HomePhone />} />
								<Route path="/home-phone/list-page" element={<ListPage />} />
								<Route path="/home-phone/consume-cellular" element={<ConsumeCellular />} />
								<Route path="/home-phone/checkout" element={<HomePhoneCheckout />} />
								<Route path="/activate" element={<Activation />} />
								<Route path="/quick-recharge" element={<QuickRecharge />} />
								<Route path="/schedule-call" element={<ScheduleCall />} />
								<Route path="/network-coverage" element={<NetworkCoverage />} />
								<Route path="/international-calling" element={<InternationalCalls />} />
								<Route path="/imei-check" element={<IMEICheck />} />
								<Route path="/about-esim" element={<AbouteSIM />} />
								<Route path="/single-network-coverage" element={<DataTopup />} />
								<Route path="/multi-network-coverage" element={<DataTopup />} />
								<Route path="/family-plans" element={<FamilyPlans />} />
								<Route path="/broadband-facts" element={<BroadbandFacts />} />
								<Route path="/digital-security" element={<DigitalSecurity />} />
								<Route path="/digital-security-selection" element={<DigitalSecuritySelection />} />
								<Route path="/sim-swap-protection" element={<SIMSwap />} />
								<Route path="/network-plan" element={<NetworkPlan />} />
								<Route path="/e911/911-emergency-services-statement" element={<Disclosure />} />
								<Route path="/network-management-policy" element={<NetworkPolicy />} />
								<Route path="/accessibility" element={<Accessibility />} />
								<Route path="/return-policy" element={<ReturnPolicy />} />
								<Route path="/terms-and-conditions" element={<TermsAndConditions />} />
								<Route path="/privacy-policy" element={<PrivacyPolicy />} />
								<Route path="/deletion-suspension-policy" element={<DeletionPolicy />} />
								<Route path="/do-not-sell" element={<DoNotSellMyData />} />
								<Route element={<SingleNetworkSelection />}>
									<Route
										path="/single-network-selection"
										element={<MakeASelection />}
									/>
									<Route
										path="/single-network-phone-type"
										element={<DeviceCompatibilty />}
									/>
									<Route
										path="/single-network-number-type"
										element={<TypeOfNumber />}
									/>
								</Route>
								<Route element={<MultiNetworkSelection />}>
									<Route
										path="/multi-network-selection"
										element={<SelectionAndCoverage />}
									/>
									<Route
										path="/multi-network-phone-type"
										element={<PhoneType />}
									/>
									<Route
										path="/multi-network-number-type"
										element={<NumberType />}
									/>
									<Route
										path="/multi-network-number-information"
										element={<NumberInformation />}
									/>
								</Route>
								<Route element={<MobileLineDetailsProvider><MyAccountLayout /></MobileLineDetailsProvider>}>
									<Route
										path="/my-account/dashboard"
										element={<Dashboard />}
									/>
									<Route
										path="/my-account/payment-history"
										element={<PaymentHistory />}
									/>
									<Route
										path="/my-account/manage-profile"
										element={<ManageProfile />}
									/>
									<Route
										path="/my-account/change-password"
										element={<ChangePassword />}
									/>
									<Route
										path="/my-account/sim-service"
										element={<SimService />}
									/>
								</Route>
								<Route path="/checkout" element={<Checkout />} />
								<Route path="/login" element={<Login />} />
								<Route path="/forgot-password" element={<ForgotPassword />} />
								<Route path="/agent/customer-dashboard" element={<CustomerDashboard />} />
								<Route path="/agent/two-factor-authentication" element={<TwoFactorAuthentication />} />
								<Route path="/agent/sim-unlock-request" element={<SimUnlockRequest />} />
								<Route path="/agent/swap-data" element={<SwapData />} />
								<Route path="/agent/swap-voice" element={<SwapVoice />} />
								<Route path="/agent/number-change" element={<NumberChange />} />
								<Route path="/agent/update-profile" element={<UpdateProfile />} />
								<Route path="/agent/update-payment-method" element={<UpdatePaymentMethod />} />
								<Route path="/agent/upgrade-plan" element={<UpgradePlan />} />
								<Route path="/agent/add-line" element={<AddLine />} />
								<Route path="/agent/create-support-ticket" element={<CreateSupportTicket />} />
							  <Route path="*" element={<NotFoundPage />} />
							</Route>
							<Route path="/agent/login" element={<AgentLogin />} />
						</Routes>
					</Elements>
					<Toaster />
				</TooltipProvider>
				{/* <GlobalStyle /> */}
			</ThemeProvider>
		</BrowserRouter>
	);
}
